import { useCallback, useMemo, useRef, useState, type FC } from "react";
import type { EditorRef } from "react-email-editor";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import WizardStepper from "src/components/reusable/WizardStepper/WizardStepper";
import type { CampaignDraft } from "src/services/CampaignDraftTypes";
import type { CampaignData } from "../../../../EngagementInterfaces";
import CampaignPageLayout from "../../../shared/CampaignPageLayout";
import CampaignFlowActions from "../../../shared/CampaignFlowActions";
import CampaignStepCard from "../../../shared/CampaignStepCard";
import AiGeneratingCampaignLoader from "src/layouts/AskDatatram/CampaignWizard/AiGeneratingCampaignLoader";
import CampaignNextButton from "../../../shared/CampaignNextButton";
import { ShowFieldErrorsContext } from "../../../shared/UseRequiredFieldError";
import CampaignDraftDialogs from "../../../shared/CampaignDraftDialogs";
import { CAMPAIGN_ANALYTICS_ROUTE } from "../../../CampaignSetup/CampaignSetupConstants";
import EmailStatusPopup from "./EmailStatusPopup";
import ScheduleCampaignModal from "./components/ScheduleCampaignModal";
import ProductCategoriesStep from "./steps/ProductCategoriesStep";
import CampaignCategoryStep from "./steps/CampaignCategoryStep";
import CampaignSubCategoryStep from "./steps/CampaignSubCategoryStep";
import AiPromptStep from "./steps/AiPromptStep";
import CampaignBasicsStep from "./steps/CampaignBasicsStep";
import ReviewDetailsStep from "./steps/ReviewDetailsStep";
import SenderRecipientsStep from "./steps/SenderRecipientsStep";
import CreateCampaignStep from "./steps/CreateCampaignStep";
import { useAiEmailWizard } from "./hooks/UseAiEmailWizard";
import { useProductCategories } from "./hooks/UseProductCategories";
import { useCampaignDelivery } from "./hooks/UseCampaignDelivery";
import { useRecommendedProducts } from "./hooks/UseRecommendedProducts";
import { EMAIL_WIZARD_STEPS, MAX_PRODUCT_CATEGORIES } from "./AiCampaignConstants";
import { saveEditorDesign } from "./EmailEditorUtils";
import type { AiCampaignStepKey } from "./AiCampaignTypes";
import "./AiCampaignWizard.scss";

const WIZARD_KEY = "campaignFlow.aiWizard";
const CREATE_STEP_INDEX = EMAIL_WIZARD_STEPS.findIndex(step => step.key === "createCampaign");
const SELECTION_STEP_KEYS: AiCampaignStepKey[] = [
  "productCategories",
  "campaignCategory",
  "campaignSubCategory",
];

interface AiCampaignWizardProps {
  campaignData: Partial<CampaignData>;
  initialDraft?: CampaignDraft | null;
}

const AiCampaignWizard: FC<AiCampaignWizardProps> = ({ campaignData, initialDraft = null }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const editorRef = useRef<EditorRef>(null);

  const {
    stepIndex,
    stepKey,
    form,
    template,
    senderDetails,
    sendTo,
    excludeList,
    audienceOptions,
    excludeOptions,
    companyName,
    isListsLoading,
    nameError,
    isGenerating,
    canProceed,
    setSendTo,
    setReplyTo,
    setExcludeList,
    updateForm,
    goNext,
    goBack,
    editStep,
    goToStep,
    furthestIndex,
    isEditingFromReview,
    subjectSuggestions,
    saveTemplateDesign,
    draft,
  } = useAiEmailWizard(campaignData, { initialDraft });

  const handleCategoriesChange = useCallback(
    (categories: string[]) => updateForm("selectedCategories", categories),
    [updateForm]
  );
  const categoriesState = useProductCategories(
    form.selectedCategories,
    handleCategoriesChange,
    true,
    MAX_PRODUCT_CATEGORIES
  );

  const {
    isSending,
    isScheduleOpen,
    deliveryStatus,
    sendNow,
    schedule,
    openSchedule,
    closeSchedule,
    clearDeliveryStatus,
  } = useCampaignDelivery({
    editorRef,
    template,
    campaignName: form.campaignName,
    subjectLine: form.subjectLine,
    senderDetails,
    replyTo: senderDetails.replyTo,
    draftId: draft.draftId,
    onDelivered: draft.close,
  });

  const [errorStepKey, setErrorStepKey] = useState<AiCampaignStepKey | null>(null);
  const [isDraftOpen, setIsDraftOpen] = useState(false);


  const revealStepErrors = useCallback(() => {
    setErrorStepKey(stepKey);
    if (SELECTION_STEP_KEYS.includes(stepKey)) {
      goNext();
    }
  }, [goNext, stepKey]);

  const isCreateStep = stepKey === "createCampaign";
  const recommendedProducts = useRecommendedProducts({
    audienceNames: senderDetails.recipients,
    excludeAudienceNames: senderDetails.exclusions,
    campaignName: form.campaignName,
    enabled: isCreateStep,
  });

  const stepperSteps = useMemo(
    () => EMAIL_WIZARD_STEPS.map(step => ({ key: step.key, title: t(step.titleKey) })),
    [t]
  );

  const reviewStepIndex = EMAIL_WIZARD_STEPS.findIndex(step => step.key === "reviewDetails");
  const completedKeys = useMemo(
    () =>
      isEditingFromReview
        ? EMAIL_WIZARD_STEPS.slice(0, reviewStepIndex).map(step => step.key)
        : undefined,
    [isEditingFromReview, reviewStepIndex]
  );
  const captionOverrides = useMemo(
    () => (isEditingFromReview ? { reviewDetails: t("wizardStepper.returnHere") } : undefined),
    [isEditingFromReview, t]
  );
  let currentCaption: string | undefined;
  if (isGenerating) {
    currentCaption = t(`${WIZARD_KEY}.generating`);
  } else if (isEditingFromReview) {
    currentCaption = t("wizardStepper.editing");
  }

  const { save: saveDraft } = draft;
  const handleSaveDraft = useCallback(async () => {
    const design = isCreateStep ? await saveEditorDesign(editorRef.current?.editor) : null;
    if (design) {
      saveTemplateDesign(design);
    }
    return saveDraft(design);
  }, [isCreateStep, saveDraft, saveTemplateDesign]);

  const handleBack = useCallback(async () => {
    if (isCreateStep) {
      const design = await saveEditorDesign(editorRef.current?.editor);
      if (design) {
        saveTemplateDesign(design);
      }
    }
    goBack();
  }, [goBack, isCreateStep, saveTemplateDesign]);

  const handleStepSelect = useCallback(
    async (index: number) => {
      if (isCreateStep) {
        const design = await saveEditorDesign(editorRef.current?.editor);
        if (design) {
          saveTemplateDesign(design);
        }
      }
      await goToStep(index);
    },
    [goToStep, isCreateStep, saveTemplateDesign]
  );

  const isStepSelectable = useCallback(
    (index: number) => index <= furthestIndex && !(index === CREATE_STEP_INDEX && index > stepIndex),
    [furthestIndex, stepIndex]
  );

  const renderStep = () => {
    switch (stepKey) {
      case "campaignDetails":
        return (
          <CampaignBasicsStep
            title={t(`${WIZARD_KEY}.basics.title`)}
            campaignName={form.campaignName}
            onCampaignNameChange={value => updateForm("campaignName", value)}
            companyName={companyName}
            sendTo={sendTo}
            excludeList={excludeList}
            audienceOptions={audienceOptions}
            excludeOptions={excludeOptions}
            isListsLoading={isListsLoading}
            nameError={nameError}
            notice={isEditingFromReview ? t(`${WIZARD_KEY}.edit.basicsNotice`) : undefined}
            onSendToChange={setSendTo}
            onExcludeListChange={setExcludeList}
          />
        );
      case "productCategories":
        return (
          <ProductCategoriesStep
            selectedCategories={form.selectedCategories}
            categoriesState={categoriesState}
            maxSelection={MAX_PRODUCT_CATEGORIES}
          />
        );
      case "campaignCategory":
        return <CampaignCategoryStep form={form} updateForm={updateForm} />;
      case "campaignSubCategory":
        return <CampaignSubCategoryStep form={form} updateForm={updateForm} />;
      case "aiPrompt":
        return <AiPromptStep form={form} updateForm={updateForm} />;
      case "reviewDetails":
        return (
          <ReviewDetailsStep
            form={form}
            sendTo={sendTo}
            excludeList={excludeList}
            audienceOptions={audienceOptions}
            senderDetails={senderDetails}
            onEdit={editStep}
          />
        );
      case "senderRecipients":
        return (
          <SenderRecipientsStep
            subjectLine={form.subjectLine}
            onSubjectLineChange={value => updateForm("subjectLine", value)}
            subjectSuggestions={subjectSuggestions}
            senderDetails={senderDetails}
            sendTo={sendTo}
            audienceOptions={audienceOptions}
            isListsLoading={isListsLoading}
            onReplyToChange={setReplyTo}
            onSendToChange={setSendTo}
          />
        );
      case "createCampaign":
        return (
          <CreateCampaignStep
            title={t(`${WIZARD_KEY}.createCampaign.title`)}
            subtitle={t(`${WIZARD_KEY}.createCampaign.subtitle`)}
            editorRef={editorRef}
            template={template}
            recommendedProducts={recommendedProducts}
            companyName={companyName}
          />
        );
      default:
        return null;
    }
  };

  return (
    <CampaignPageLayout
      stickyChrome
      onExit={() => navigate(CAMPAIGN_ANALYTICS_ROUTE)}
      stepper={
        <WizardStepper
          steps={stepperSteps}
          currentIndex={isGenerating ? CREATE_STEP_INDEX : stepIndex}
          currentCaption={currentCaption}
          completedKeys={completedKeys}
          captionOverrides={captionOverrides}
          isStepSelectable={isStepSelectable}
          onStepSelect={isGenerating || isSending ? undefined : handleStepSelect}
        />
      }
    >
      {isGenerating ? (
        <CampaignStepCard title={form.campaignName.trim() || t(`${WIZARD_KEY}.details.title`)}>
          <AiGeneratingCampaignLoader />
        </CampaignStepCard>
      ) : (
        <ShowFieldErrorsContext.Provider value={errorStepKey === stepKey}>
          {renderStep()}
        </ShowFieldErrorsContext.Provider>
      )}

      {!isGenerating && (
        <CampaignFlowActions
          sticky
          onBack={handleBack}
          backDisabled={isSending}
          backLabel={
            stepIndex === 0 || isEditingFromReview ? t("campaignFlow.common.cancel") : undefined
          }
          note={isEditingFromReview ? t(`${WIZARD_KEY}.edit.note`) : undefined}
        >
          {!isEditingFromReview && (
            <button
              type="button"
              className="button button--outline campaign-flow__action"
              onClick={() => setIsDraftOpen(true)}
              disabled={isSending}
            >
              {t("campaignFlow.common.saveDraft")}
            </button>
          )}
          {isCreateStep ? (
            <>
              <button
                type="button"
                className="button button--primary campaign-flow__action"
                onClick={openSchedule}
                disabled={isSending}
              >
                {t(`${WIZARD_KEY}.createCampaign.scheduleCampaign`)}
              </button>
              <button
                type="button"
                className="button button--primary campaign-flow__action"
                onClick={sendNow}
                disabled={isSending}
              >
                {isSending
                  ? t(`${WIZARD_KEY}.createCampaign.sending`)
                  : t(`${WIZARD_KEY}.createCampaign.sendCampaign`)}
              </button>
            </>
          ) : (
            <CampaignNextButton
              canProceed={canProceed}
              onNext={goNext}
              onBlockedClick={revealStepErrors}
            >
              {t(
                isEditingFromReview
                  ? `${WIZARD_KEY}.edit.saveReturn`
                  : `${WIZARD_KEY}.next`
              )}
            </CampaignNextButton>
          )}
        </CampaignFlowActions>
      )}

      {isScheduleOpen && (
        <ScheduleCampaignModal
          isSubmitting={isSending}
          onCancel={closeSchedule}
          onSchedule={schedule}
        />
      )}

      <CampaignDraftDialogs
        guardActive={!deliveryStatus && (isCreateStep || draft.hasUnsavedChanges)}
        isDraftOpen={isDraftOpen}
        isSaving={draft.isSaving}
        onCloseDraft={() => setIsDraftOpen(false)}
        onSaveDraft={handleSaveDraft}
      />

      {deliveryStatus && (
        <EmailStatusPopup
          type={deliveryStatus.type}
          date={deliveryStatus.date}
          time={deliveryStatus.time}
          onClose={clearDeliveryStatus}
        />
      )}
    </CampaignPageLayout>
  );
};

export default AiCampaignWizard;
